import React, { FC, memo, useCallback, useEffect, useState, useMemo } from 'react'
import { RecurlyError } from '@recurly/recurly-js'
import * as Sentry from '@sentry/nextjs'
import { useSubscribeAnalyticsEvent } from '@pff-consumer/analytics/hooks/useSubscribeAnalyticsEvent'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { CheckoutResponseError } from '@pff-consumer/schema'
import { getErrorType } from '@pff-consumer/utils'
import { Modal } from '../../modal/modal.web'
import { ModalProps } from '../../modal/modal.props'
import { useStyles } from './checkout-error-modal.styles'

const { useEmail, useCheckoutError, usePaymentError } = checkoutHooks

const HEADER_TEXT = 'FLAG ON THE PLAY!'
const SUPPORT_URL = 'https://profootballfocussupport.zendesk.com/hc/en-us/requests/new'

const SupportLink: FC = memo(() => {
  const styles = useStyles()
  return (
    <a
      className={styles.link}
      href={SUPPORT_URL}
    >
      here
    </a>
  )
})

/**
 * Get the ideal modal props based on the type of error we are seeing.
 */
const useErrorData = (
  paymentError: RecurlyError | undefined,
  checkoutError: CheckoutResponseError | undefined
): Partial<ModalProps> => {
  const errorType = getErrorType(paymentError, checkoutError)

  return useMemo(() => {
    switch (errorType) {
      case 'GENERIC':
        return {
          buttonText: 'Try Again',
          closeable: true,
          description: (
            <>
              Looks like something went wrong. Please try again or reach out <SupportLink /> for help.
            </>
          ),
          heading: HEADER_TEXT,
        }
      case 'CHECKOUT_PAYMENT_ERROR':
      case 'RECURLY_PAYMENT_ERROR':
        return {
          buttonText: 'Try Again',
          closeable: true,
          description: 'Payment declined. Please update your payment information and try again.',
          heading: HEADER_TEXT,
        }
      case 'INTERNAL_ERROR':
        return {
          buttonText: 'Try Again',
          closeable: false,
          description: (
            <>
              Looks like something went wrong. Please try again or reach out <SupportLink /> for help.
            </>
          ),
          heading: HEADER_TEXT,
          handleButtonClick: () => {
            // Since we got an internal error lets reload the whole page
            window.location.reload()
          },
        }
      case 'ALREADY_SUBSCRIBED':
        return {
          heading: HEADER_TEXT,
          description: (
            <>
              There was an issue processing your payment. If you have an account, please log in. For additional help,
              contact us <SupportLink />
            </>
          ),
          closeable: false,
          buttonText: 'Log In',
          handleButtonClick: () => {
            window.open(`/login?to=${encodeURIComponent(process.env.PFF_DOMAIN!)}`, '_self')
          },
        }
      case 'UNKNOWN_APPLE_PAY_ERROR':
        return {
          heading: HEADER_TEXT,
          description: (
            <>
              There was an unexpected error processing your payment with ApplePay. Please try again or try a different
              method. If the error persists contact us <SupportLink />
            </>
          ),
          closeable: true,
          buttonText: 'Try Again',
        }
      case undefined:
        return {
          closeable: true,
        }
    }
  }, [errorType])
}

export const CheckoutErrorModal: React.FC = memo(() => {
  const [showModal, setShowModal] = useState<boolean>(false)
  const checkoutError = useCheckoutError()
  const paymentError = usePaymentError()
  const email = useEmail()
  const { sendSubscribeAnalyticsEvent } = useSubscribeAnalyticsEvent()

  const { heading, description, closeable, buttonText, handleButtonClick } = useErrorData(paymentError, checkoutError)

  const onClose = useCallback(() => {
    setShowModal(false)
  }, [])

  useEffect(() => {
    if (paymentError) {
      // There is no reason to call Sentry.captureException or log the error here
      // This already happens in onError which triggers the opening of this modal
      sendSubscribeAnalyticsEvent('Transaction Failed', {
        error_source: 'recurly_js',
        error_type: paymentError?.code,
        email,
      })
      setShowModal(true)
    }
  }, [paymentError, email, sendSubscribeAnalyticsEvent])

  useEffect(() => {
    if (checkoutError) {
      console.error(checkoutError)
      Sentry.captureException(new Error(JSON.stringify(checkoutError)))
      sendSubscribeAnalyticsEvent('Transaction Failed', {
        error_source: 'checkout_api',
        error_type: checkoutError?.error,
        email,
      })
      setShowModal(true)
    }
  }, [checkoutError, email, sendSubscribeAnalyticsEvent])

  if (!checkoutError && !paymentError) {
    return null
  }

  return (
    <Modal
      closeable={closeable || true}
      buttonText={buttonText || ''}
      description={description}
      handleButtonClick={handleButtonClick || onClose}
      heading={heading || ''}
      onClose={onClose}
      showModal={showModal}
    />
  )
})
